import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search, Copy, Network } from "lucide-react";
import { toast } from "sonner";
import { getAppRpcEndpoints, type RpcNetworkEndpoint } from "@/api";
import { copyToClipboard as copyText } from "@/utils";
import { DataTableStateRow } from "../components/data-table/DataTableStateRow";
import { DataTableLoadingOverlay } from "../components/data-table/DataTableLoadingOverlay";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";

/** Abbreviate long endpoint URLs for table display. */
function abbreviateEndpoint(url: string, head = 16, tail = 8): string {
  if (url.length <= head + tail + 3) return url;
  return `${url.slice(0, head)}...${url.slice(-tail)}`;
}

const SEARCH_DEBOUNCE_MS = 300;
/** RPC 网络列表每页条数（客户端分页） */
const RPC_PAGE_SIZE = 10;

export function RpcList() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [networks, setNetworks] = useState<RpcNetworkEndpoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const hasLoadedRef = useRef(false);

  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const load = async () => {
      if (hasLoadedRef.current) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result = await getAppRpcEndpoints(id, {
          search: debouncedSearch || undefined,
        });
        if (cancelled) return;
        if (result.ok) {
          setNetworks(result.data.networks);
        } else {
          setNetworks([]);
          toast.error(tRef.current("common.fetchFailed"));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("[RpcList] Failed to load endpoints:", error);
          setNetworks([]);
          toast.error(tRef.current("common.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          hasLoadedRef.current = true;
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [id, debouncedSearch]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(networks.length / RPC_PAGE_SIZE));
    if (page > tp) setPage(tp);
  }, [networks.length, page]);

  const totalPages = Math.max(1, Math.ceil(networks.length / RPC_PAGE_SIZE));
  const pagedNetworks = networks.slice(
    (page - 1) * RPC_PAGE_SIZE,
    page * RPC_PAGE_SIZE,
  );

  const handleCopy = (value: string) => {
    void copyText(value).then(() => {
      toast.success(t("rpcList.toastCopied"));
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-2">
        <Link
          to="/keys"
          className="p-2 -ml-2 text-slate-400 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-[#141517] transition-colors w-fit"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <nav className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-1">
            <Link
              to="/keys"
              className="hover:text-emerald-600 dark:hover:text-[#00FFA3] transition-colors"
            >
              {t("rpcList.breadcrumbKeys")}
            </Link>
            <span aria-hidden>/</span>
            <span className="text-slate-700 dark:text-slate-300">
              {t("rpcList.breadcrumbRpc")}
            </span>
          </nav>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t("rpcList.title")}
          </h1>
          {id ? (
            <p className="text-slate-500 dark:text-slate-400 font-mono text-xs mt-1 tracking-tight">
              {id}
            </p>
          ) : null}
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="search"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          placeholder={t("rpcList.searchPlaceholder")}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#141517] text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:focus:ring-[#00FFA3]/30"
        />
      </div>

      <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto relative">
          {isRefreshing && <DataTableLoadingOverlay />}
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th className="px-6 py-4 whitespace-nowrap">{t("rpcList.colName")}</th>
                <th className="px-2 py-4 whitespace-nowrap">{t("rpcList.colRequests24h")}</th>
                <th className="px-2 py-4 whitespace-nowrap">{t("rpcList.colCreditsDeducted")}</th>
                <th className="px-2 py-4 whitespace-nowrap">{t("rpcList.colNetwork")}</th>
                <th className="px-2 py-4 whitespace-nowrap">{t("rpcList.colHttps")}</th>
                <th className="pl-2 pr-6 py-4 whitespace-nowrap">{t("rpcList.colWss")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              <DataTableStateRow
                colSpan={6}
                loading={isLoading}
                isEmpty={!isLoading && networks.length === 0}
                emptyIcon={<Network className="h-8 w-8" />}
                emptyTitle={
                  debouncedSearch
                    ? t("rpcList.emptyNoMatch")
                    : t("rpcList.emptyTitle")
                }
                emptyHint={
                  debouncedSearch ? t("rpcList.tryDifferentSearch") : undefined
                }
              />
              {!isLoading &&
                pagedNetworks.map((row) => (
                  <tr
                    key={row.network}
                    className="hover:bg-slate-100/70 dark:hover:bg-[#141517]/80 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-900 dark:text-white">
                      {row.displayName}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-slate-700 dark:text-slate-300 tabular-nums">
                      {row.usage.requests24h.toLocaleString()}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-slate-700 dark:text-slate-300 tabular-nums">
                      {row.usage.creditsDeducted.toLocaleString()}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-slate-600 dark:text-slate-400">
                      {row.network}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 max-w-[220px]">
                        <span
                          className="truncate font-mono text-xs text-slate-600 dark:text-slate-400"
                          title={row.httpEndpoint}
                        >
                          {abbreviateEndpoint(row.httpEndpoint)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopy(row.httpEndpoint)}
                          className="p-1.5 shrink-0 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-[#00FFA3] dark:hover:bg-[#00FFA3]/5 rounded-lg transition-colors"
                          title={t("rpcList.copyEndpoint")}
                        >
                          <Copy className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                    <td className="pl-2 pr-6 py-4 whitespace-nowrap">
                      {row.wssEndpoint ? (
                        <div className="flex items-center gap-1.5 max-w-[220px]">
                          <span
                            className="truncate font-mono text-xs text-slate-600 dark:text-slate-400"
                            title={row.wssEndpoint}
                          >
                            {abbreviateEndpoint(row.wssEndpoint)}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopy(row.wssEndpoint!)}
                            className="p-1.5 shrink-0 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-[#00FFA3] dark:hover:bg-[#00FFA3]/5 rounded-lg transition-colors"
                            title={t("rpcList.copyEndpoint")}
                          >
                            <Copy className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {networks.length > 0 && (
          <div className="px-6 pb-4 pt-0">
            <TablePaginationFooter
              page={page}
              totalPages={totalPages}
              totalItems={networks.length}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
